<script setup>
import { computed, onMounted, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { getI18n } from '@/plugins/i18n'
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup'
import { DxForm } from 'devextreme-vue/form'

import * as dataGridColumn from '@/composables/data-grid-column'
import { codeNameDisplayExpr, customerDisplayExpr } from '@/composables/label'
import { useDocumentIdentifiers } from '@/composables/document'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useGarageStore } from '@/stores/garage.store'
import { useNotify } from '@/composables/notify'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'
import { useSaleStore } from '@/stores/sale.store'

import CarServiceNumberBox from '@/components/shared/CarServiceNumberBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'

const { notifySuccess } = useNotify()
const { checkCustomerPrivacyAsync } = useDocumentIdentifiers()

const webSiteStore = useWebSiteStore()
const garageStore = useGarageStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const saleStore = useSaleStore()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  selectedItem: {
    type: Object,
    default: () => {},
  },
  generateDocumentType: {
    type: Number,
    default: null,
  },
  generatingDocumentType: {
    type: Number,
    default: null,
  },
  nextIdFunction: {
    type: Function,
    default: null,
  },
  generateFunction: {
    type: Function,
    default: null,
  },
  formEditors: {
    type: Array,
    default: () => ([]),
  }
})

const i18n = getI18n()

const emit = defineEmits(['update:visible', 'created', 'cancel'])
const popupVisible = useVModel(props, 'visible', emit)
const currentCarService = ref(null)
const form = ref(null)
const formData = ref(props.selectedItem)
const documentGenerateType = portalStore.getLocalizedDocumentGenerateTypes().find(d => d.id === props.generateDocumentType)
const documentGeneratingType = portalStore.getLocalizedDocumentGeneratingTypes().find(d => d.id === props.generatingDocumentType)
const sdiCodesByGenerateDocumentType = {
  [portalStore.documentGenerateEnum.IMMEDIATE_INVOICE]: ['TD01'],
  [portalStore.documentGenerateEnum.DEFERRED_INVOICE]: ['TD01', 'TD24', 'TD25'],
  [portalStore.documentGenerateEnum.CREDIT_NOTE]: ['TD04'],
}

const getSaleReasonSdiDocumentType = (saleReason) => portalStore.electronicInvoiceSdiDocumentTypes.find(item => item.id === saleReason?.sdi_document_type)

const getDefaultSdiDocumentType = (saleReason, documentType) => {
  const allowedCodes = sdiCodesByGenerateDocumentType[documentType]

  if (!allowedCodes) {
    return null
  }

  const saleReasonSdiDocumentType = getSaleReasonSdiDocumentType(saleReason)
  if (saleReasonSdiDocumentType) { return saleReasonSdiDocumentType.id }

  if (allowedCodes.length === 1) {
    return portalStore.getLocalizedElectronicInvoiceSdiDocumentTypes().find(item => item.code === allowedCodes[0])?.id || null
  }

  return null
}

const computeHasCarService = (garageReasonRef) => {
  const currentGarageReason = garageStore.garageReasons.find(i => i.id === garageReasonRef)
  const interventionType = garageStore.interventionTypes.find(
    i => i.id === currentGarageReason?.intervention_type_ref
  )
  return interventionType?.has_car_service
}

onMounted(async() => {
  formData.value = props.selectedItem
  if (formData.value) {
    const res = await props.nextIdFunction(null, props.selectedItem?.sale_document_type)
    const { series, number_mnemonic } = res.data

    const documentNumberMnemonicEditorInstance = form.value.instance?.getEditor('document_number_mnemonic').instance()
    const documentSeriesEditorInstance = form.value.instance?.getEditor('series').instance()
    documentNumberMnemonicEditorInstance.option('value', number_mnemonic)

    formData.value['specific_sdi_document_type'] = getDefaultSdiDocumentType(
      saleStore.saleReasons.find(reason => reason.id === formData.value['sale_reason_ref']),
      props.generateDocumentType
    )
    formData.value['source_document_type'] = props.generatingDocumentType
    formData.value['source_customer_id'] = formData.value['customer_id']
    formData.value['customer_id'] = formData.value['invoice_to_customer_id'] || formData.value['billing_customer_id'] || formData.value['customer_id']
    formData.value['source_deposit'] = formData.value['deposit']
    formData.value['source_series'] = formData.value['series']
    formData.value['source_payment_type_ref'] = formData.value['payment_type_ref']
    formData.value['source_garage_reason_ref'] = formData.value['garage_reason_ref']
    formData.value['source_sale_reason_ref'] = formData.value['sale_reason_ref']

    formData.value['date'] = new Date().toISOString().slice(0, 10)
    documentSeriesEditorInstance.option('value', series)
    formData.value.series = series

    formData.value['payment_type_ref'] = formData.value?.invoicing_customer?.accounting_payment_type_ref || formData.value['source_payment_type_ref']

    if (!computeHasCarService(props.selectedItem['garage_reason_ref'])) {
      formData.value.car_service = null
      form.value?.instance?.repaint()
    }

    if (formData.value?.vehicle_id) {
      const res = await registryStore.api.getVehicleNextCarServiceAsync(formData.value.vehicle_id)
      currentCarService.value = res.data
    }
  }
  form.value?.instance?.validate()
})

const middleIconItem = {
  itemType: 'simple',
  colSpan: 1,
  cssClass: 'middle-icon-wrapper',
  template: () => {
    const el = document.createElement('div')
    el.innerHTML = `<i class="mdi mdi-chevron-right mdi-36px middle-icon"></i>`
    return el
  }
}

const buildSourceReasonColumn = () => {
  if ([portalStore.documentGeneratingEnum.DDT, portalStore.documentGeneratingEnum.DEFERRED_INVOICE, portalStore.documentGeneratingEnum.IMMEDIATE_INVOICE].includes(props.generatingDocumentType)) {
    return {
      dataField: 'source_sale_reason_ref',
      label: { text: i18n.t('common.reason') },
      editorType: 'dxSelectBox',
      editorOptions: {
        sort: 'code',
        dataSource: { store: saleStore.getLocalizedSaleReasons(), sort: 'code' },
        valueExpr: 'id',
        displayExpr: codeNameDisplayExpr,
        readOnly: true,
      },
    }
  }
  return {
    dataField: 'source_garage_reason_ref',
    label: { text: i18n.t('common.workReason') },
    editorType: 'dxSelectBox',
    editorOptions: {
      sort: 'code',
      dataSource: { store: garageStore.getLocalizedGarageReasons(), sort: 'code' },
      valueExpr: 'id',
      displayExpr: codeNameDisplayExpr,
      readOnly: true,
    },
  }
}

const buildDestinationReasonColumn = () => {
  if (props.generateDocumentType === portalStore.documentGenerateEnum.WORKSHEET) {
    return [{
      dataField: 'garage_reason_ref',
      label: { text: i18n.t('common.workReason') },
      editorType: 'dxSelectBox',
      editorOptions: {
        sort: 'code',
        dataSource: { store: garageStore.getLocalizedGarageReasons(), sort: 'code' },
        searchEnabled: true,
        searchExpr: ['code', 'name'],
        searchMode: 'contains',
        valueExpr: 'id',
        displayExpr: codeNameDisplayExpr,
        readOnly: false,
        onValueChanged: async(e) => {
          formData.value.garage_reason_ref = e.value
          const currentGarageReason = garageStore.garageReasons.find(i => i.id === e.value)

          const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)

          const hasCarService = interventionType?.has_car_service
          form.value.instance.getEditor('car_service')?.option('disabled', !hasCarService)

          const isCarService = interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE
          if (!hasCarService || !isCarService) {
            formData.value.car_service = null
            return
          }
          if (formData.value?.vehicle_id) {
            const res = await registryStore.api.getVehicleNextCarServiceAsync(formData.value.vehicle_id)
            formData.value.car_service = currentCarService.value = res.data
            form.value?.instance?.repaint()
            return
          }

          formData.value.car_service = 1
        },
      },
      validationRules: [{ type: 'required' }],
    },
    {
      label: { text: i18n.t('common.carServiceNumber') },
      dataField: 'car_service',
      dataType: 'number',
      template: 'carServiceNumberEditor',
      validationRules: [toggleReadOnlyValidation('car_service')],
    }]
  }
  return [{
    dataField: 'sale_reason_ref',
    label: { text: i18n.t('common.reason') },
    editorType: 'dxSelectBox',
    editorOptions: {
      sort: 'code',
      dataSource: { store: saleStore.getLocalizedSaleReasons(), sort: 'code' },
      searchEnabled: true,
      searchExpr: ['code', 'name'],
      searchMode: 'contains',
      valueExpr: 'id',
      displayExpr: codeNameDisplayExpr,
      readOnly: false,
      onValueChanged: (e) => {
        formData.value.sale_reason_ref = e.value
        const saleReason = saleStore.saleReasons.find(reason => reason.id === e.value)
        formData.value.specific_sdi_document_type = getDefaultSdiDocumentType(saleReason, props.generateDocumentType)
      },
    },
    validationRules: [{ type: 'required' }],
  }]
}

const toggleReadOnlyValidation = (field) => ({
  type: 'custom',
  message: i18n.t('common.required'),
  reevaluate: true,
  validationCallback: (e) => {
    const value = formData.value.garage_reason_ref
    const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)

    const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)

    if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE) {
      return e.value != null && e.value !== ''
    }

    return true
  }
})

const buildPropsFormColumn = () => {
  return props.formEditors.filter(editor => editor.dataField !== 'car_service').map(editor => {
    return {
      ...editor,
      validationRules: editor.validationRules || [],
    }
  })
}

const getFilteredSdiDocumentTypes = (documentType, saleReason) => {
  const allowedCodes = sdiCodesByGenerateDocumentType[documentType]

  if (!allowedCodes) {
    return []
  }

  const saleReasonSdiDocumentType = getSaleReasonSdiDocumentType(saleReason)
  if (saleReasonSdiDocumentType) {
    return portalStore.getLocalizedElectronicInvoiceSdiDocumentTypes().filter(item => item.id === saleReasonSdiDocumentType.id)
  }

  return portalStore.getLocalizedElectronicInvoiceSdiDocumentTypes().filter(item => allowedCodes.includes(item.code))
}

const buildDestinationSpecificSdiColumn = () => {
  if (props.generateDocumentType === portalStore.documentGenerateEnum.IMMEDIATE_INVOICE ||
      props.generateDocumentType === portalStore.documentGenerateEnum.DEFERRED_INVOICE ||
      props.generateDocumentType === portalStore.documentGenerateEnum.CREDIT_NOTE) {
    return {
      dataField: 'specific_sdi_document_type',
      label: { text: i18n.t('common.electronicInvoiceSdiDocumentType') },
      editorType: 'dxSelectBox',
      editorOptions: {
        sort: 'code',
        dataSource: {
          store: getFilteredSdiDocumentTypes(
            props.generateDocumentType,
            saleStore.saleReasons.find(reason => reason.id === formData.value?.sale_reason_ref)
          ),
          sort: 'code'
        },
        valueExpr: 'id',
        displayExpr: codeNameDisplayExpr,
        readOnly: false,
      },
    }
  }
  return {}
}

const formItems = computed(() => [
  {
    itemType: 'group',
    cssClass: 'dx-form-group-no-background',
    colCount: 11,
    items: [
      {
        itemType: 'group',
        colSpan: 5,
        items: [
          {
            itemType: 'group',
            caption: `${i18n.t('common.generatingDocumentRef')} - ${documentGeneratingType?.name}`,
            colCount: 4,
            items: [
              { dataField: 'generating_document_date', label: { text: i18n.t('common.date')}, editorType: 'dxDateBox', editorOptions: { readOnly: true }, colSpan: 2 },
              { dataField: 'source_series', label: { text: i18n.t('common.series')}, width: '6rem', alignment: 'left', editorOptions: { readOnly: true } },
              { dataField: 'number_mnemonic', label: { text: i18n.t('common.number')}, width: '6rem', alignment: 'left', editorOptions: { readOnly: true } },
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.mainInfo'),
            colCount: 2,
            items: [
              {
                dataField: 'source_customer_id',
                label: { text: i18n.t('common.customer')},
                editorType: 'dxSelectBox',
                editorOptions: {
                  dataSource: { store: registryStore.customers, sort: 'name' },
                  valueExpr: 'id',
                  displayExpr: customerDisplayExpr,
                  readOnly: true,
                },
              },
              {
                dataField: 'source_deposit',
                label: { text: i18n.t('common.deposit') },
                editorType: 'dxSelectBox',
                editorOptions: {
                  sort: 'code',
                  dataSource: { store: registryStore.getLocalizedDeposits() },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  readOnly: true
                }
              },
              {
                dataField: 'source_payment_type_ref',
                label: { text: i18n.t('common.paymentType') },
                editorType: 'dxLookup',
                editorOptions: {
                  sort: 'code',
                  dataSource: { store: webSiteStore.getLocalizedPaymentTypes() },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  readOnly: true
                }
              },
              buildSourceReasonColumn(),
            ]
          },
        ]
      },
      middleIconItem,
      {
        itemType: 'group',
        colSpan: 5,
        items: [
          {
            itemType: 'group',
            caption: `${i18n.t('common.generatedDocumentRef')} - ${documentGenerateType?.name}`,
            colCount: 4,
            items: [
              {
                dataField: 'date',
                label: { text: i18n.t('common.date')},
                editorType: 'dxDateBox',
                colSpan: 2,
                editorOptions: {
                  onValueChanged: (e) => {
                    if (e.value) {
                      const year = new Date(e.value).getFullYear()
                      formData.value.series = year
                    }
                  }
                },
                validationRules: [
                  { type: 'required' },
                  {
                    type: 'custom',
                    message: i18n.t('common.dateMustBeGreaterThanGeneratingDocumentDate'),
                    validationCallback: (e) => {
                      const date = new Date(e.value)
                      const minDate = new Date(formData.value.generating_document_date)

                      date.setHours(0, 0, 0, 0)
                      minDate.setHours(0, 0, 0, 0)
                      return date >= minDate
                    }
                  }
                ]
              },
              { dataField: 'series', label: { text: i18n.t('common.series')}, width: '6rem', alignment: 'left', editorOptions: { readOnly: true } },
              { dataField: 'document_number_mnemonic', label: { text: i18n.t('common.number')}, editorType: 'dxNumberBox', editorOptions: { min: 1 }, width: '6rem', alignment: 'left', validationRules: [{ type: 'required' }] },
            ]
          },
          {
            itemType: 'group',
            caption: i18n.t('common.mainInfo'),
            colCount: 2,
            items: [
              dataGridColumn.useCustomerColumn({
                label: { text: i18n.t('common.owner') },
                dataField: 'customer_id',
                template: 'customerEditor',
                editorType: 'dxSelectBox',
              }),
              {
                dataField: 'deposit',
                label: { text: i18n.t('common.deposit') },
                editorType: 'dxSelectBox',
                editorOptions: {
                  sort: 'code',
                  dataSource: { store: registryStore.getLocalizedDeposits() },
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  readOnly: true
                }
              },
              {
                dataField: 'payment_type_ref',
                label: { text: i18n.t('common.paymentType') },
                editorType: 'dxSelectBox',
                editorOptions: {
                  sort: 'code',
                  dataSource: { store: webSiteStore.getLocalizedPaymentTypes() },
                  searchEnabled: true,
                  searchExpr: ['code', 'name'],
                  searchMode: 'contains',
                  valueExpr: 'id',
                  displayExpr: codeNameDisplayExpr,
                  readOnly: false
                },
                validationRules: [{ type: 'required' }],
              },
              ...buildDestinationReasonColumn(),
              buildDestinationSpecificSdiColumn(),
              ...buildPropsFormColumn()
            ]
          },
        ]
      },
    ]
  }
])

const cancelButtonOptions = ref({
  text: i18n.t('common.cancel'),
  onClick: () => {
    popupVisible.value = false
    emit('cancel')
  }
})

const generateButtonOptions = ref({
  text: i18n.t('common.generate'),
  onClick: async(e) => {
    const validateResult = form.value?.instance?.validate()
    if (validateResult?.isValid) {
      const currentData = structuredClone(formData.value)
      if (props.generateDocumentType !== portalStore.documentGenerateEnum.WORKSHEET) {
        await checkCustomerPrivacyAsync(currentData?.customer_id)
      }
      props?.generateFunction(currentData)
      .then(async() => {
        notifySuccess(i18n.t('common.success'))
        popupVisible.value = false
        emit('created')
        form.value = null
      })
    }
  }
})

const onHiding = () => {
  popupVisible.value = false
  emit('cancel')
}

const onFormContentReady = async() => {
  const value = formData.value.garage_reason_ref
  if (!value) return

  const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)
  const interventionType = garageStore.interventionTypes.find(
    i => i.id === currentGarageReason?.intervention_type_ref
  )

  const hasCarService = interventionType?.has_car_service
  form.value.instance.getEditor('car_service')?.option('disabled', !hasCarService)
}

</script>
<template>
  <dx-popup
    v-model:visible="popupVisible"
    height="auto"
    width="70rem"
    :hide-on-outside-click="true"
    :title="`${i18n.t('common.generatedDocumentRef')} - ${documentGenerateType?.name}`"
    @hiding="onHiding"
  >
    <dx-form
      ref="form"
      class="q-mt-md"
      height="auto"
      :items="formItems"
      :form-data="formData"
      @content-ready="onFormContentReady"
    >
      <template #customerEditor>
        <customer-drop-down-box
          :key="formData.customer_id"
          :data="{data: {customer: registryStore.customers.find(c => c.id === formData.customer_id)} || null}"
          :value="formData.customer_id"
          show-clear-button
          @value-changed="(e) => formData.customer_id = e.value?.id"
        />
      </template>
      <template #carServiceNumberEditor="{ data: cellInfo }">
        <car-service-number-box
          :data="cellInfo"
          :value="formData.car_service"
          :garage-reason-ref="formData.garage_reason_ref"
          :current-car-service="currentCarService"
        />
      </template>
    </dx-form>
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="generateButtonOptions"
    />
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="cancelButtonOptions"
    />
  </dx-popup>
</template>
<style lang="scss">
  .middle-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .middle-icon {
    color: $primary;
  }
</style>
